import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search
} from 'lucide-react';
import YellowCtaBox from '../../components/YellowCtaBox';
import SEO from '../../components/SEO';
import winchImg from '../../images/images.png';
import './Areas.css';
import { areas, regionsByGovernorate } from '../../data/areas.js';
import { corridors } from '../../data/corridors';
import articlesData from '../../data/articles.json';
import { toArabicSlug } from '../../data/areaSlugMapping';





const Areas = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const phoneNumber = '01044844492';

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const normalizeArabic = (text) => {
    return text
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/أ|إ|آ/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/ء/g, '');
  };

  const filteredAreas = useMemo(() => {
    const normalizedSearch = normalizeArabic(searchTerm);

    let areasToFilter = [];

    if (selectedRegion === 'all') {
      areasToFilter = areas;
    } else {
      const region = regionsByGovernorate.find(r => r.name === selectedRegion);
      areasToFilter = region ? region.areas : [];
    }

    return areasToFilter.filter(area =>
      normalizeArabic(area).includes(normalizedSearch)
    );
  }, [searchTerm, selectedRegion]);


  return (
    <>
      <SEO
        title="مناطق الخدمة | ونش انقاذ في القاهرة والجيزة والمحاور السريعة"
        description="دليل مناطق تغطية ونش انقاذ محمد بحر: القاهرة والجيزة والمدن الجديدة والمحاور السريعة (33 منطقة رئيسية). خصم 50% ووصول فوري 24 ساعة."
        path="/areas"
      />
      <style>
        {`
          /* Areas Page Styles */
          .areas-hero {
            background: linear-gradient(135deg, #0a2c61 0%, #1976d2 100%);
            color: white;
            padding: 4rem 2rem;
            text-align: center;
            position: relative;
            overflow: hidden;
          }

          .areas-hero::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
            opacity: 0.3;
          }

          .areas-hero-content {
            position: relative;
            z-index: 2;
            max-width: 800px;
            margin: 0 auto;
          }

          .areas-hero-title {
            font-size: 3rem;
            font-weight: bold;
            margin-bottom: 1rem;
            color: #ffc107;
          }

          .areas-hero-subtitle {
            font-size: 1.3rem;
            margin-bottom: 2rem;
            opacity: 0.9;
          }

          .areas-stats {
            display: flex;
            justify-content: center;
            gap: 3rem;
            margin-top: 2rem;
          }

          .stat-item {
            text-align: center;
          }

          .stat-number {
            font-size: 2.5rem;
            font-weight: bold;
            color: #ffc107;
            display: block;
          }

          .stat-label {
            font-size: 1rem;
            opacity: 0.8;
          }

          /* Main Content */
          .areas-main {
            max-width: 1200px;
            margin: 0 auto;
            padding: 4rem 2rem;
          }

          .search-section {
            background: white;
            border-radius: 1.5rem;
            box-shadow: 0 4px 24px rgba(0,0,0,0.1);
            padding: 2rem;
            margin-bottom: 3rem;
            position: relative;
            overflow: hidden;
          }

          .search-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 4px;
            height: 100%;
            background: linear-gradient(135deg, #ffc107 0%, #ffb100 100%);
          }

          .search-header {
            text-align: center;
            margin-bottom: 2rem;
          }

          .search-title {
            font-size: 2rem;
            font-weight: bold;
            color: #0a2c61;
            margin-bottom: 1rem;
          }

          .search-description {
            color: #666;
            font-size: 1.1rem;
            line-height: 1.6;
          }

          .search-controls {
            display: flex;
            gap: 1rem;
            margin-bottom: 2rem;
            flex-wrap: wrap;
            justify-content: center;
          }

          .search-input {
            flex: 1;
            min-width: 300px;
            padding: 1rem 1.5rem;
            border: 2px solid #e9ecef;
            border-radius: 0.8rem;
            font-size: 1.1rem;
            transition: all 0.3s ease;
            direction: rtl;
          }

          .search-input:focus {
            outline: none;
            border-color: #ffc107;
            box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1);
          }

          .region-filter {
            padding: 1rem 1.5rem;
            border: 2px solid #e9ecef;
            border-radius: 0.8rem;
            font-size: 1.1rem;
            background: white;
            cursor: pointer;
            transition: all 0.3s ease;
            direction: rtl;
          }

          .region-filter:focus {
            outline: none;
            border-color: #ffc107;
          }

          .search-results {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            color: #666;
            font-size: 1rem;
          }

          /* Areas Grid */
          .areas-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
            gap: 2rem;
            margin-top: 2rem;
          }

          .area-card {
            background: white;
            border-radius: 1.2rem;
            box-shadow: 0 4px 24px rgba(0,0,0,0.1);
            overflow: hidden;
            position: relative;
            transition: all 0.3s ease;
            border: 2px solid transparent;
          }

          .area-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 12px 40px rgba(0,0,0,0.15);
            border-color: #ffc107;
          }

          .area-image {
            position: relative;
            width: 100%;
            height: 180px;
            overflow: hidden;
          }

          .area-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            filter: brightness(0.85);
            transition: transform 0.3s ease;
          }

          .area-card:hover .area-image img {
            transform: scale(1.05);
          }

          .area-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(180deg, rgba(10, 34, 58, 0.8) 0%, rgba(10, 34, 58, 0.2) 80%);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
            z-index: 2;
          }

          .area-brand {
            font-weight: bold;
            font-size: 1.2rem;
            letter-spacing: 1px;
            margin-bottom: 0.5rem;
          }

          .area-tagline {
            font-size: 0.9rem;
            margin-bottom: 0.5rem;
            opacity: 0.9;
          }

          .area-badge {
            background: rgba(255, 193, 7, 0.9);
            color: #0a2c61;
            border-radius: 1.2rem;
            padding: 0.3rem 1rem;
            font-weight: bold;
            font-size: 0.9rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          }

          .area-content {
            padding: 1.5rem;
            text-align: center;
          }

          .area-name {
            background: #0a3977;
            color: white;
            font-weight: bold;
            font-size: 1.3rem;
            padding: 1rem;
            text-align: center;
            letter-spacing: 0.5px;
            border-bottom: 1px solid #eaeaea;
          }

          .area-subtitle {
            color: #666;
            font-size: 1.1rem;
            padding: 1rem;
            font-weight: 500;
            letter-spacing: 0.5px;
            background: white;
            border-bottom-left-radius: 1.2rem;
            border-bottom-right-radius: 1.2rem;
          }

          /* No Results */
          .no-results {
            text-align: center;
            padding: 3rem 2rem;
            color: #666;
          }

          .no-results-icon {
            font-size: 4rem;
            color: #ddd;
            margin-bottom: 1rem;
          }

          .no-results-title {
            font-size: 1.5rem;
            font-weight: bold;
            margin-bottom: 1rem;
            color: #333;
          }

          .no-results-description {
            font-size: 1.1rem;
            line-height: 1.6;
          }

          /* Contact CTA */
          .contact-cta {
            background: linear-gradient(135deg, #ffc107 0%, #ffb100 100%);
            color: #0a2c61;
            border-radius: 1.5rem;
            padding: 3rem 2rem;
            text-align: center;
            margin-top: 3rem;
          }

          .cta-title {
            font-size: 2rem;
            font-weight: bold;
            margin-bottom: 1rem;
          }

          .cta-description {
            font-size: 1.2rem;
            margin-bottom: 2rem;
            opacity: 0.9;
          }

          .cta-button {
            background: #0a2c61;
            color: white;
            border: none;
            border-radius: 0.8rem;
            padding: 1rem 2rem;
            font-size: 1.1rem;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s ease;
            text-decoration: none;
            display: inline-block;
          }

          .cta-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          }

          /* Responsive Design */
          @media (max-width: 768px) {
            .areas-hero {
              padding: 3rem 1rem;
            }

            .areas-hero-title {
              font-size: 2.2rem;
            }

            .areas-hero-subtitle {
              font-size: 1.1rem;
            }

            .areas-stats {
              flex-direction: column;
              gap: 1.5rem;
            }

            .areas-main {
              padding: 2rem 1rem;
            }

            .search-section {
              padding: 1.5rem;
            }

            .search-title {
              font-size: 1.6rem;
            }

            .search-controls {
              flex-direction: column;
            }

            .search-input {
              min-width: auto;
            }

            .areas-grid {
              grid-template-columns: 1fr;
            }

            .contact-cta {
              padding: 2rem 1.5rem;
            }

            .cta-title {
              font-size: 1.6rem;
            }
          }

          @media (max-width: 480px) {
            .areas-hero-title {
              font-size: 1.8rem;
            }

            .search-title {
              font-size: 1.4rem;
            }

            .area-card {
              margin: 0 0.5rem;
            }
          }
        `}
      </style>

      {/* Hero Section */}
      <section className="areas-hero">
        <div className="areas-hero-content">
          <h1 className="areas-hero-title">مناطق الخدمة</h1>
          <p className="areas-hero-subtitle">
            نقدم خدمات ونش الانقاذ في جميع أنحاء مصر مع تغطية شاملة للطرق السريعة والمناطق الحضرية
          </p>
          <div className="areas-stats">
            <div className="stat-item">
              <span className="stat-number">{areas.length}+</span>
              <span className="stat-label">منطقة خدمة</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">10</span>
              <span className="stat-label">دقائق للوصول</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">24/7</span>
              <span className="stat-label">خدمة متواصلة</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="areas-main">
        {/* Search Section */}
        <section className="search-section">
          <div className="search-header">
            <h2 className="search-title">البحث عن منطقة الخدمة</h2>
            <p className="search-description">
              ابحث عن منطقتك أو اختر من القائمة أدناه للحصول على خدمة ونش الانقاذ في أسرع وقت
            </p>
          </div>

          <div className="search-controls">
            <input
              type="text"
              placeholder="ابحث عن منطقتك..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="region-filter"
            >
              <option value="all">جميع المناطق ({areas.length})</option>
              {regionsByGovernorate.map(region => (
                <option key={region.name} value={region.name}>
                  {region.name} ({region.areas.length})
                </option>
              ))}
            </select>

          </div>

          <div className="search-results">
            <Search size={20} />
            <span>تم العثور على {filteredAreas.length} منطقة</span>
          </div>
        </section>

        {/* Highway Corridors (T053) */}
        <section className="corridors-section" style={{ marginBottom: '2rem' }}>
          <h2 style={{ color: '#0a2c61', fontSize: '1.4rem', marginBottom: '0.5rem', textAlign: 'center' }}>محاور الطرق السريعة</h2>
          <p style={{ textAlign: 'center', color: '#555', marginBottom: '1rem' }}>خدمة إنقاذ على أهم المحاور في القاهرة الكبرى</p>
          <div className="corridors-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem', direction: 'rtl' }}>
            {Object.values(corridors).map((c) => (
              <Link key={c.id} to={`/corridor/${encodeURIComponent(c.id)}`} style={{ background: '#0a2c61', color: '#fff', padding: '0.7rem 1rem', borderRadius: '0.7rem', textDecoration: 'none', fontWeight: 600, textAlign: 'center' }}>
                ونش {c.id}
              </Link>
            ))}
          </div>
        </section>

        {/* Areas Grid */}
        {filteredAreas.length > 0 ? (
          <section className="areas-grid">
            {filteredAreas.map((area, idx) => (
              <Link
                key={idx}
                to={`/areas/${toArabicSlug(area)}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className="area-card">
                  <div className="area-image">
                    <img src={winchImg} alt="ونش انقاذ" />
                    <div className="area-overlay">
                      <div className="area-brand">MOHAMED BAHR</div>
                      <div className="area-tagline">أسرع وأرخص ونش انقاذ في مصر</div>
                      <span className="area-badge">خدمة على مدار الساعة</span>
                    </div>
                  </div>
                  <div className="area-name">ونش انقاذ {area}</div>
                  <div className="area-subtitle">ونش انقاذ</div>
                </div>
              </Link>
            ))}
          </section>
        ) : (
          <section className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3 className="no-results-title">لم يتم العثور على نتائج</h3>
            <p className="no-results-description">
              لا توجد مناطق تطابق بحثك. جرب البحث بكلمات مختلفة أو اتصل بنا مباشرة للحصول على المساعدة.
            </p>
          </section>
        )}

        {/* Contact CTA */}
        <section className="contact-cta">
          <h2 className="cta-title">لا تجد منطقتك؟</h2>
          <p className="cta-description">
            اتصل بنا مباشرة وسنصل إليك في أي مكان في مصر
          </p>
          <a href={`tel:${phoneNumber}`} className="cta-button">
            اتصل الآن: {phoneNumber}
          </a>
        </section>

        {/* Guides and Dedicated Articles Section */}
        <section className="areas-guides-section" style={{
          margin: '3rem 0 1rem',
          padding: '2rem',
          background: '#ffffff',
          borderRadius: '1.5rem',
          border: '2px solid #e3f0ff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          direction: 'rtl'
        }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0a2c61', marginBottom: '0.8rem' }}>
            أدلة ومقالات إنقاذ السيارات في مختلف المناطق
          </h2>
          <p style={{ color: '#64748b', fontSize: '1.05rem', marginBottom: '1.5rem', lineHeight: 1.7 }}>
            نوفر دليلاً ميدانياً شاملاً وتفصيلياً لكل منطقة ومحور رئيسي لمساعدتك عند الأعطال الطارئة ومعرفة أسعار الإنقاذ وأوقات الوصول:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.8rem', marginBottom: '1.5rem' }}>
            <Link to="/articles/اسعار-ونش-انقاذ-2026" style={guideBadgeStyle}>أسعار ونش الإنقاذ 2026</Link>
            <Link to="/articles/winch-tagamoa-fifth-settlement-guide" style={guideBadgeStyle}>دليل التجمع الخامس</Link>
            <Link to="/articles/winch-ninety-street-new-cairo-guide" style={guideBadgeStyle}>دليل شارع التسعين</Link>
            <Link to="/articles/winch-nasr-city-towing-service" style={guideBadgeStyle}>دليل مدينة نصر</Link>
            <Link to="/articles/winch-abbas-el-akkad-guide" style={guideBadgeStyle}>دليل عباس العقاد</Link>
            <Link to="/articles/winch-makram-ebeid-guide" style={guideBadgeStyle}>دليل مكرم عبيد</Link>
            <Link to="/articles/winch-heliopolis-towing-service" style={guideBadgeStyle}>دليل مصر الجديدة</Link>
            <Link to="/articles/winch-roxy-guide" style={guideBadgeStyle}>دليل روكسي</Link>
            <Link to="/articles/winch-moqattam-towing-service" style={guideBadgeStyle}>دليل المقطم</Link>
            <Link to="/articles/winch-maadi-guide" style={guideBadgeStyle}>دليل المعادي</Link>
            <Link to="/articles/winch-6th-of-october-guide" style={guideBadgeStyle}>دليل 6 أكتوبر</Link>
            <Link to="/articles/winch-sheikh-zayed-guide" style={guideBadgeStyle}>دليل الشيخ زايد</Link>
            <Link to="/articles/winch-ring-road-towing-service" style={guideBadgeStyle}>دليل الطريق الدائري</Link>
            <Link to="/articles/winch-suez-road-towing-service" style={guideBadgeStyle}>دليل طريق السويس</Link>
          </div>
          <Link to="/articles" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#0a2c61',
            color: '#ffffff',
            padding: '0.7rem 1.8rem',
            borderRadius: '2rem',
            fontWeight: 800,
            textDecoration: 'none',
            fontSize: '1rem'
          }}>
            تصفح جميع المقالات والأدلة الإرشادية ({articlesData.articles.length} دليلاً) ⬅
          </Link>
        </section>
      </div>

      <YellowCtaBox />
    </>
  );
};

const guideBadgeStyle = {
  background: '#f1f5f9',
  border: '1px solid #cbd5e1',
  color: '#0a2c61',
  padding: '0.45rem 1rem',
  borderRadius: '2rem',
  fontSize: '0.92rem',
  fontWeight: 700,
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
};

export default Areas; 